import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { fuelImportService } from '../../services/logistics/fuel-import';
import LoadingSpinner from '../ui/LoadingSpinner';
import FuelManualAssignmentModal from './FuelManualAssignmentModal';
import VehicleMappingModal from './VehicleMappingModal';
import VehicleMappingManagementModal from './VehicleMappingManagementModal';
import toast from 'react-hot-toast';
import './fuel-import-modal-fixes.css';

const FuelImportModal = ({ isOpen, onClose, onImportComplete }) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  
  const [step, setStep] = useState('upload'); // upload, vehicle_mapping, review, processing, complete
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importData, setImportData] = useState(null);
  const [error, setError] = useState(null);
  const [showManualAssignment, setShowManualAssignment] = useState(false);
  const [showVehicleMapping, setShowVehicleMapping] = useState(false);
  const [showVehicleMappingManagement, setShowVehicleMappingManagement] = useState(false);

  const [selectedShipmentDetails, setSelectedShipmentDetails] = useState(null);

  // Format date for display (always Georgian DD/MM/YYYY)
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format date and time for display
  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format number for display
  const formatNumber = (num) => {
    if (!num) return '0';
    return parseFloat(num).toFixed(2);
  };

  // Reset modal state when opening/closing
  React.useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setFile(null);
      setImportData(null);
      setError(null);
      setShowVehicleMapping(false);
    }
  }, [isOpen]);

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  }, []);

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Handle file selection
  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setError(null);
  };

  // Process the uploaded file
  const processFile = async () => {
    if (!file || !currentCompany?.id || !user?.uid) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fuelImportService.processImport(
        currentCompany.id,
        file,
        user.uid
      );

      setImportData(result);
      
      // Check if vehicle mapping is required
      if (result.requiresVehicleMapping) {
        setStep('vehicle_mapping');
        setShowVehicleMapping(true);
      } else {
        setStep('review');
      }
    } catch (error) {
      console.error('Error processing file:', error);
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Finalize the import
  const finalizeImport = async () => {
    if (!importData || !currentCompany?.id || !user?.uid) return;

    setLoading(true);
    setStep('processing');

    try {
      const result = await fuelImportService.finalizeImport(
        currentCompany.id,
        importData,
        user.uid
      );

      setStep('complete');
      
      // Create success message including odometer sync results
      let successMessage = `Successfully imported ${result.importedCount} fuel transactions`;
      if (result.odometerReadingsCreated > 0) {
        successMessage += ` and created ${result.odometerReadingsCreated} odometer readings`;
      }
      
      toast.success(successMessage);
      
      // Notify parent component
      if (onImportComplete) {
        onImportComplete(result);
      }
    } catch (error) {
      console.error('Error finalizing import:', error);
      setError(error.message);
      toast.error(error.message);
      setStep('review');
    } finally {
      setLoading(false);
    }
  };

  // Handle manual assignment (opens unmatched review modal)
  const handleManualAssignment = () => {
    setShowManualAssignment(true);
  };

  // Handle vehicle mapping confirmation
  const handleVehicleMappingConfirm = async (confirmedMappings) => {
    if (!importData || !currentCompany?.id || !user?.uid) return;

    setLoading(true);
    setShowVehicleMapping(false);

    try {
      const result = await fuelImportService.processImportWithMappings(
        currentCompany.id,
        importData,
        confirmedMappings,
        user.uid
      );

      setImportData(result);
      setStep('review');
      toast.success(t('fuel.vehicle_mappings_saved'));
    } catch (error) {
      console.error('Error processing vehicle mappings:', error);
      setError(error.message);
      toast.error(error.message);
      setShowVehicleMapping(true);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fuel-import-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('fuel.import_fuel_data')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content p-6">
          {step === 'upload' && (
            <div className="space-y-6">
              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      {file ? file.name : t('fuel.drop_file_or_click')}
                    </span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      accept=".xlsx,.xls"
                      onChange={handleFileInputChange}
                    />
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    {t('fuel.supported_formats')} (.xlsx, .xls)
                  </p>
                  <p className="text-xs text-gray-500">
                    {t('fuel.max_file_size')} (10MB)
                  </p>
                </div>
              </div>

              {/* File Info */}
              {file && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <DocumentArrowUpIcon className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      {t('fuel.import_instructions')}
                    </h3>
                    <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
                      <li>{t('fuel.instruction_1')}</li>
                      <li>{t('fuel.instruction_2')}</li>
                      <li>{t('fuel.instruction_3')}</li>
                      <li>{t('fuel.instruction_4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'review' && importData && (
            <div className="space-y-6">
              {/* Import Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {importData.totalRows}
                  </div>
                  <div className="text-sm text-blue-800">
                    {t('fuel.total_rows')}
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-600">
                    {importData.matched.length}
                  </div>
                  <div className="text-sm text-green-800">
                    {t('fuel.auto_matched')}
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-600">
                    {importData.unmatched.length}
                  </div>
                  <div className="text-sm text-yellow-800">
                    {t('fuel.unmatched')}
                  </div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-600">
                    {importData.duplicatesSkipped}
                  </div>
                  <div className="text-sm text-red-800">
                    {t('fuel.duplicates_skipped')}
                  </div>
                </div>
              </div>

              {/* Match Rate */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {t('fuel.match_rate')}
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {(importData.matchRate * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${importData.matchRate * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Preview Data */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('fuel.preview_data')}
                </h3>
                
                {/* Enhanced Matched Transactions */}
                {importData.matched.length > 0 && (
                  <div>
                    <div className="mb-2">
                      <h4 className="text-sm font-medium text-green-800">
                        📊 المعاملات المطابقة ({importData.matched.length})
                      </h4>
                    </div>
                    <div className="bg-white border rounded-lg overflow-hidden max-h-80">
                      <div className="overflow-x-auto overflow-y-auto max-h-80">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-green-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                رقم الفاتورة
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                المركبة
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                تاريخ التعبئة
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                اللترات
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                التكلفة
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                الشحنة المطابقة
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                تاريخ الشحنة
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {importData.matched.map((transaction, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-sm text-gray-900 font-medium">
                                  #{transaction.invoice_number || 'N/A'}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">
                                  <div>
                                    <div className="font-medium">{transaction.mappedVehiclePlate || transaction.vehicle_plate}</div>
                                    {transaction.mappedVehiclePlate && transaction.mappedVehiclePlate !== transaction.vehicle_plate && (
                                      <div className="text-xs text-blue-600">
                                        {transaction.vehicle_plate} → {transaction.mappedVehiclePlate}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">
                                  {formatDateTime(transaction.fill_time)}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">
                                  {formatNumber(transaction.liters)} لتر
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900 font-medium">
                                  {formatNumber(transaction.cost)} ريال
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  {transaction.matchedShipment ? (
                                    <button
                                      onClick={() => setSelectedShipmentDetails(transaction.matchedShipment)}
                                      className="text-green-600 hover:text-green-800 font-medium hover:underline"
                                    >
                                      📦 #{transaction.matchedShipment.trackingNumber}
                                    </button>
                                  ) : (
                                    <div className="text-green-600 flex items-center">
                                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                                      مطابق تلقائياً
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-600">
                                  {transaction.matchedShipment ? (
                                    formatDateTime(
                                      transaction.matchedShipment.deliveryDate || 
                                      transaction.matchedShipment.pickupDate || 
                                      transaction.matchedShipment.createdAt
                                    )
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Enhanced Unmatched Transactions Preview */}
                {importData.unmatched.length > 0 && (
                  <div>
                    <div className="mb-2">
                      <h4 className="text-sm font-medium text-yellow-800">
                        ⚠️ المعاملات غير المطابقة ({importData.unmatched.length})
                      </h4>
                      <p className="text-xs text-yellow-700 mt-1">
                        هذه المعاملات تحتاج إلى مطابقة يدوية أو تعيين مركبات
                      </p>
                    </div>
                    <div className="bg-white border rounded-lg overflow-hidden max-h-80">
                      <div className="overflow-x-auto overflow-y-auto max-h-80">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-yellow-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                رقم الفاتورة
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                المركبة
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                تاريخ التعبئة
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                اللترات
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                التكلفة
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">
                                سبب عدم المطابقة
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {importData.unmatched.map((transaction, index) => (
                              <tr key={index} className="hover:bg-yellow-50">
                                <td className="px-3 py-2 text-sm text-gray-900 font-medium">
                                  #{transaction.invoice_number || 'N/A'}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">
                                  <div>
                                    <div className="font-medium">{transaction.mappedVehiclePlate || transaction.vehicle_plate}</div>
                                    {transaction.mappedVehiclePlate && transaction.mappedVehiclePlate !== transaction.vehicle_plate && (
                                      <div className="text-xs text-blue-600">
                                        {transaction.vehicle_plate} → {transaction.mappedVehiclePlate}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">
                                  {formatDateTime(transaction.fill_time)}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">
                                  {formatNumber(transaction.liters)} لتر
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900 font-medium">
                                  {formatNumber(transaction.cost)} ريال
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <div className="space-y-1">
                                    {/* Primary reason */}
                                    <div className="flex items-center text-yellow-600">
                                      <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                                      <span className="text-xs">
                                        {transaction.unmatchedReason || 'لا توجد شحنات مطابقة'}
                                      </span>
                                    </div>
                                    
                                    {/* Detailed debugging info */}
                                    <div className="text-xs text-gray-500 space-y-1">
                                      {!transaction.hasVehicleMatch && (
                                        <div className="text-red-600">❌ مركبة غير موجودة في النظام</div>
                                      )}
                                      {transaction.hasVehicleMatch && !transaction.hasTimeMatch && (
                                        <div className="text-orange-600">⏰ لا توجد شحنات في نفس الوقت</div>
                                      )}
                                      {transaction.potentialMatches && transaction.potentialMatches > 0 && (
                                        <div className="text-blue-600">🔍 {transaction.potentialMatches} شحنات محتملة</div>
                                      )}
                                      {transaction.nearestShipmentDays && (
                                        <div className="text-gray-600">📅 أقرب شحنة: {transaction.nearestShipmentDays} أيام</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    {/* Unmatched Summary */}
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="text-sm text-yellow-800">
                        <div className="font-medium mb-2">ملخص المعاملات غير المطابقة:</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                          <div>
                            <span className="font-medium">مركبات غير موجودة:</span>
                            <span className="ml-1">
                              {importData.unmatched.filter(t => !t.hasVehicleMatch).length}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">مشاكل توقيت:</span>
                            <span className="ml-1">
                              {importData.unmatched.filter(t => t.hasVehicleMatch && !t.hasTimeMatch).length}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">أسباب أخرى:</span>
                            <span className="ml-1">
                              {importData.unmatched.filter(t => t.hasVehicleMatch && t.hasTimeMatch).length}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="text-center py-8">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-lg font-medium text-gray-900">
                {t('fuel.processing_import')}
              </p>
              <p className="text-sm text-gray-500">
                {t('fuel.please_wait')}
              </p>
            </div>
          )}

          {step === 'complete' && (
            <div className="text-center py-8">
              <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {t('fuel.import_complete')}
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                {t('fuel.import_success_message')}
              </p>
              
              {/* Import Results Summary */}
              {importData && (
                <div className="mt-6 bg-gray-50 rounded-lg p-4 text-left max-w-md mx-auto">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    {t('fuel.import_summary', 'Import Summary')}
                  </h4>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {t('fuel.transactions_imported', 'Fuel Transactions:')}
                      </span>
                      <span className="font-medium text-gray-900">
                        {importData.importedCount || 0}
                      </span>
                    </div>
                    
                    {importData.odometerReadingsCreated > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {t('fuel.odometer_readings_created', 'Odometer Readings:')}
                        </span>
                        <span className="font-medium text-green-600">
                          {importData.odometerReadingsCreated}
                        </span>
                      </div>
                    )}
                    
                    {importData.odometerSyncErrors && importData.odometerSyncErrors.length > 0 && (
                      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <div className="flex items-center">
                          <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600 mr-1" />
                          <span className="text-xs text-yellow-800">
                            {t('fuel.odometer_sync_warnings', 
                              `${importData.odometerSyncErrors.length} odometer sync warnings`
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - Fixed positioning and visibility */}
        <div className="modal-footer">
          <div className="flex items-center justify-between">
            <div className="action-buttons">
              {step === 'review' && importData?.unmatched.length > 0 && (
                <button
                  onClick={handleManualAssignment}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 flex items-center space-x-2"
                >
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <span>{t('fuel.review_unmatched')} ({importData.unmatched.length})</span>
                </button>
              )}
              
              {step === 'review' && (
                <button
                  onClick={() => setShowVehicleMappingManagement(true)}
                  className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 border border-purple-300 rounded-md hover:bg-purple-200 flex items-center space-x-2"
                >
                  <TruckIcon className="h-4 w-4" />
                  <span>{t('fuel.manage_vehicle_mappings')}</span>
                </button>
              )}
            </div>
            
            <div className="action-buttons">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {step === 'complete' ? t('common.close') : t('common.cancel')}
              </button>
              
              {step === 'upload' && file && (
                <button
                  onClick={processFile}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center min-w-[140px] justify-center"
                >
                  {loading && <LoadingSpinner size="sm" className="mr-2" />}
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                  <span>{t('fuel.process_file')}</span>
                </button>
              )}
              
              {step === 'review' && importData && (
                <button
                  onClick={finalizeImport}
                  disabled={loading}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center min-w-[160px] justify-center"
                >
                  {loading && <LoadingSpinner size="sm" className="mr-2" />}
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  <span>{t('fuel.apply_changes')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Manual Assignment Modal */}
      <FuelManualAssignmentModal
        isOpen={showManualAssignment}
        onClose={() => setShowManualAssignment(false)}
        onAssignmentComplete={() => {
          // Refresh import data or close modal
          setShowManualAssignment(false);
          toast.success('Assignments updated successfully');
        }}
        unmatchedTransactionsData={importData?.unmatched || []}
      />

      <VehicleMappingModal
        isOpen={showVehicleMapping}
        onClose={() => setShowVehicleMapping(false)}
        onConfirm={handleVehicleMappingConfirm}
        mappingData={importData?.vehicleMappingData}
        loading={loading}
      />

      <VehicleMappingManagementModal
        isOpen={showVehicleMappingManagement}
        onClose={() => setShowVehicleMappingManagement(false)}
        onMappingsUpdated={() => {
          // Optionally refresh import data or show success message
          toast.success('Vehicle mappings updated successfully');
        }}
      />

      {/* Shipment Details Modal */}
      {selectedShipmentDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                تفاصيل الشحنة #{selectedShipmentDetails.trackingNumber}
              </h3>
              <button
                onClick={() => setSelectedShipmentDetails(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto">
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 border-b pb-2">معلومات أساسية</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">رقم الشحنة:</span>
                        <span className="font-medium">{selectedShipmentDetails.trackingNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">الحالة:</span>
                        <span className="font-medium">{selectedShipmentDetails.status || 'غير محدد'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">المنشأ:</span>
                        <span className="font-medium">{selectedShipmentDetails.origin}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">الوجهة:</span>
                        <span className="font-medium">{selectedShipmentDetails.destination}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 border-b pb-2">المركبة والسائق</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">لوحة المركبة:</span>
                        <span className="font-medium">{selectedShipmentDetails.vehiclePlate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">السائق:</span>
                        <span className="font-medium">{selectedShipmentDetails.driverName || 'غير محدد'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 border-b pb-2">التواريخ</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">تاريخ الإنشاء:</span>
                      <span className="font-medium">{formatDateTime(selectedShipmentDetails.createdAt)}</span>
                    </div>
                    {selectedShipmentDetails.pickupDate && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">تاريخ الاستلام:</span>
                        <span className="font-medium">{formatDateTime(selectedShipmentDetails.pickupDate)}</span>
                      </div>
                    )}
                    {selectedShipmentDetails.deliveryDate && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">تاريخ التسليم:</span>
                        <span className="font-medium">{formatDateTime(selectedShipmentDetails.deliveryDate)}</span>
                      </div>
                    )}
                    {selectedShipmentDetails.updatedAt && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">آخر تحديث:</span>
                        <span className="font-medium">{formatDateTime(selectedShipmentDetails.updatedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Details */}
                {(selectedShipmentDetails.notes || selectedShipmentDetails.description) && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 border-b pb-2">تفاصيل إضافية</h4>
                    {selectedShipmentDetails.description && (
                      <div>
                        <span className="text-gray-600 text-sm">الوصف:</span>
                        <p className="mt-1 text-sm">{selectedShipmentDetails.description}</p>
                      </div>
                    )}
                    {selectedShipmentDetails.notes && (
                      <div>
                        <span className="text-gray-600 text-sm">ملاحظات:</span>
                        <p className="mt-1 text-sm">{selectedShipmentDetails.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Technical Details */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 border-b pb-2">معلومات تقنية</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500">
                    <div className="flex justify-between">
                      <span>معرف الشحنة:</span>
                      <span className="font-mono">{selectedShipmentDetails.id}</span>
                    </div>
                    {selectedShipmentDetails.companyId && (
                      <div className="flex justify-between">
                        <span>معرف الشركة:</span>
                        <span className="font-mono">{selectedShipmentDetails.companyId.substring(0, 8)}...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 bg-gray-50 border-t">
              <button
                onClick={() => setSelectedShipmentDetails(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelImportModal;